import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, LinearProgress, MenuItem, Select, Snackbar, TextField,
} from '@mui/material';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import { Root, useStyles } from './styled';
import VirtualizedView, { RowGrid } from 'shared/components/VirtualizedView';
import { columnTypes } from 'shared/components/tables';
import { debounce, uniq } from 'lodash';
import {
  deleteInvestmentMutation,
  moveToPortfolioMutation,
  updateInvestmentMutation,
} from 'shared/apollo/mutations';
import {
  keyIdForAmountCategory10,
  keyIdForAmountCategory5,
  keyIdForStage,
  keyIdForStatus,
} from './Grouping';
import { removeConnectionsMemoized, useClient, useQuery } from 'shared/apollo';
import { searchToQuery, useSearch } from 'shared/components/makeNav';
import { useDispatch, useSelector } from 'react-redux';
import {
  useHomeRequest,
  useNewInvitation,
  usePortfolioSelect,
  useServices,
} from 'shared/apollo';
import { ConfirmationDialog } from 'shared/components';
import { DialogButton } from 'shared/containers/form';
import DropDownMenu from 'shared/components/DropDownMenu';
import ExportIcon from '@mui/icons-material/ImportExport';
import { GroupHeader } from 'shared/components/GroupItem/GroupItem';
import Header from './Header/Header';
import HomeIntroModal from '../../../components/HomeIntroModal';
import ImpactReceiptDrawer from '../../../components/ImpactReceiptDrawer';
import InvitationPopup from '../../../components/InvitationPopup';
import ManagerDetails from '../../../components/ManagerDetails';
import NewDonation from '../../../components/NewDonation';
import NewPortfolio from '../../../components/NewPortfolio';
import PropTypes from 'prop-types';
import { Submit } from 'shared/containers/form';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { allUsers } from 'shared/apollo/queries';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import { getInvitationStatus } from 'shared/components/RowCardDetail/RowCardDetail';
import { getMe } from '../../../actions';
import { groupBy } from 'shared/components/tables';
import styled from 'styled-components';
import { tableCols } from './constants';
import useHash from 'shared/utils/useHash';
import { useUser } from 'shared/auth';

const timing = {
  beforeQuery: null,
  afterQuery: null,
  render0: null,
  render1: null,
};

function addTiming(tag, deps) {
  if (!timing[tag]) {
    if (deps && !deps.every(dep => timing[dep])) {
      return false;
    }
    timing[tag] = Date.now();
    return true;
  }

  return false;
}

const groupByFieldMap = {
  byNpo: 'grantee.name',
  byPortfolio: 'name',
  byManager: 'investment_relationship_full_name',
  byStatus: 'combinedStatus',
  byStage: 'reportStage',
  byService: 'report.service',
  byDonationSizeA: 'amountCategory5',
  byDonationSizeB: 'amountCategory100',
  byDonationSizeC: 'amountCategory500',
  byDonationSizeD: 'amountCategory10',
};

const keyIdByFieldMap = {
  byStatus: keyIdForStatus,
  byDonationSizeA: keyIdForAmountCategory5,
  byDonationSizeD: keyIdForAmountCategory10,
  byStage: keyIdForStage,
};

const groupByField = slug => groupByFieldMap[camelCase(slug)];

const titleMappers = {
  byNpo: value => value || 'No NPO assigned',
  byPortfolio: value => value || 'No portfolio assigned',
  byManager: value => value || 'No manager assigned',
  byStatus: value => value || 'No status assigned',
  byStage: value => value,
  byDonationSizeA: value => value || '(no donation size)',
  byDonationSizeB: value => value || '(no donation size)',
  byDonationSizeC: value => value || '(no donation size)',
  byDonationSizeD: value => value || '(no donation size)',
  byDonationSize4: value => `Quartile ${value}`,
  byDonationSize5: value => `Quantile ${value}`,
  byService: value => value || 'Service',
};

const makeGroupTitleMapper = grouping => value => {
  return titleMappers[camelCase(grouping)](value);
};

const quickFilterFields = [
  'name',
  'relationshipManager.firstName',
  'investment_relationship_full_name',
  'grantee.name',
  'programName',
  'report.service',
  'combinedStatus',
  'investment_relationship_manager_email',
  'investment_relationship_first_name',
  'investment_relationship_last_name',
  'managerEmail',
];

function matchQuickFilter(obj, filter) {
  filter = filter.toLowerCase();
  return quickFilterFields.some(field =>
    String(get(obj, field))
      .toLowerCase()
      .includes(filter),
  );
}

const groupByPercentageNames = {
  4: ['Top 25%', 'Top 25-50%', 'Bottom 50%', 'Bottom 25%'],
  5: ['Top 20%', 'Top 40%', 'Middle 20%', 'Bottom 40%', 'Bottom 20%'],
};

function groupByPercentage(data, buckets) {
  if (!data) {
    return data;
  }

  const sorted = data.slice().sort((a, b) => a.amount - b.amount);

  return new Array(buckets).fill(0).map((_, index) => ({
    key: groupByPercentageNames[buckets][index],
    values: sorted.slice(
      Math.round((sorted.length / buckets) * index),
      Math.round((sorted.length / buckets) * (index + 1)),
    ),
  }));
}

const filterFields = {
  npo: 'grantee.name',
  portfolio: 'name',
  manager: 'investment_relationship_full_name',
  status: 'combinedStatus',
  stage: 'reportStage',
  service: 'report.service',
  'donation-size': 'amountCategory5',
  'donation-size-a': 'amountCategory5',
  'donation-size-b': 'amountCategory100',
  'donation-size-c': 'amountCategory500',
  'donation-size-d': 'amountCategory10',
  year: 'year',
  archive: 'portfolioStatus',
};

const SubmitLink = styled(Submit).attrs({
  alwaysEnabled: true,
  variant: 'text',
})``;

const serviceTypeItems = [
  { value: 'service', label: 'Selected Service Type' },
  { value: 'intervention', label: 'Primary Intervention Type' },
];

const ButtonLower = styled(Button)`
  margin-right 5px !important;
  span {
    text-transform: none;
  }
`;

export const HomeMain = ({
  match: {
    params: { group, view },
  },
  customPortal,
}) => {
  addTiming('beforeQuery');

  const classes = useStyles();
  const dispatch = useDispatch();
  const { query, isLoading, refresh } = useHomeRequest();
  const { portfolios } = usePortfolioSelect();
  const partnerQueueNames = useMemo(
    () => new Set(portfolios.filter(p => p.isPartnerQueue).map(p => p.name)),
    [portfolios],
  );
  const { services } = useServices();
  const { me } = useSelector(({ profile: { me } }) => ({ me }));

  const accountsData = useQuery(allUsers);
  const accounts = accountsData
    ? removeConnectionsMemoized(accountsData.allUsers).map(
        ({ dbId, firstName, lastName, email }) => ({
          id: parseInt(dbId),
          name: `${firstName} ${lastName} (${email})`,
        }),
      )
    : [];

  const currentDrawer = useSelector(({ drawer }) => drawer);
  const [showIntro, setShowIntro] = useState(false);
  const [selectedPorfolioId, setSelectedPorfolioId] = useState();
  const [showNewPortfolioDrawer, setShowNewPortfolioDrawer] = useState(false);
  const [showNewDonationDrawer, setShowNewDonationDrawer] = useState(false);
  const [donationInitialValues, setDonationInitialValues] = useState();
  const [showManagerDrawer, setShowManagerDrawer] = useState(false); // eslint-disable-line
  const [showImpactReceiptDrawer, setShowImpactReceiptDrawer] = useState(false);
  const [irTitle, setIrTitle] = useState();
  const [selectedInvestmentId, setSelectedInvestmentId] = useState();
  const [showContactManagerFail, setShowContactManagerFail] = useState(false);
  const [confirmation, setConfirmation] = useState();
  const [yearFilter, setYearFilter] = useState(0); // eslint-disable-line
  const [filter, setFilter] = useState();

  const changeFilter = useCallback(value => {
    setFilter(value);
  }, []);
  const debouncedSetFilter = useMemo(() => debounce(changeFilter, 500), [
    changeFilter,
  ]);
  const handleSearchInput = useCallback(
    (event, value) => {
      debouncedSetFilter(value);
    },
    [debouncedSetFilter],
  );

  const handleCloseIntro = hideIntroNextTime => {
    localStorage.setItem(
      'hideIntroNextTime',
      JSON.stringify(hideIntroNextTime),
    );
    setShowIntro(false);
  };

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  /*useEffect(() => {
    const timeout = setTimeout(() => {
      const hideIntroNextTime = JSON.parse(localStorage.getItem('hideIntroNextTime'))
      if (!hideIntroNextTime) {
        setShowIntro(true)
      }
    }, 1000)
    return () => clearTimeout(timeout)
  }, [])*/

  const { search: searchParams, pathname } = useLocation();

  const user = useUser();
  const effectiveUser = user?.effectiveUser;
  const search = useSearch();
  const quickFilter = search['quick-filter'];
  const [filters, setFilters] = useState([]);

  const data =
    query &&
    query
      .filter(obj => !filter || matchQuickFilter(obj, filter))
      .filter(obj => (quickFilter === 'overdue' ? obj.overdueTask : true))
      .filter(obj =>
        quickFilter === 'feedback'
          ? [
              'Awaiting Funder Feedback',
              'Awaiting Funder Verification',
            ].includes(obj.combinedStatus)
          : true,
      )
      .filter(obj =>
        quickFilter === 'unsent' ? obj.combinedStatus === 'Unnotified' : true,
      )
      .filter(obj =>
        quickFilter === 'mine'
          ? obj.relationshipManagerId ===
            effectiveUser?.attributes['custom:user_id']
          : true,
      )
      .filter(obj => (yearFilter ? obj.year === yearFilter : true))
      .filter(
        obj =>
          !filters.length ||
          filters.every(
            ({ filter, value }) => {
              const fieldValue = get(obj, filterFields[filter]);
              return filter === 'year'
                ? Number(fieldValue) === Number(value)
                : fieldValue === value;
            }
          )
      );

  const portfoliosList = data.map(({ portfolioId: id, name }) => ({
    name,
    id,
  }));
  const userId = effectiveUser.attributes['custom:user_id'];
  useEffect(() => {
    const key = `User${userId}.donorHomeUrl`;
    localStorage.setItem(key, `${pathname}${searchParams}`);
  }, [searchParams, pathname, userId]);
  const { push } = useHistory();
  const handleClearPageSettings = useCallback(() => {
    const key = `User${userId}.donorHomeUrl`;
    localStorage.removeItem(key);
    push('/donor/home');
  }, [userId, push]);
  const counter = useMemo(() => data?.length, [data]);

  const [isSortDescending, setIsSortDescending] = useState(false);
  const handleToggleSort = useCallback(() => {
    setIsSortDescending(state => !state);
  }, []);
  const [serviceType, setServiceType] = useState('service');

  const groups0 = /^(by-donation-size-\d)$/.test(group)
    ? group === 'by-donation-size-4'
      ? groupByPercentage(data, 4)
      : groupByPercentage(data, 5)
    : group === 'by-service' && serviceType === 'intervention'
    ? groupBy(data, 'primaryInterventionType')
    : groupBy(data, groupByField(group), keyIdByFieldMap[camelCase(group)]);
  let groups = isSortDescending ? groups0.reverse() : groups0;

  /*
    Filter the groups of investments per portfolio to only included the valid
    investments, so, we're not going to show portfolios with a default un-exiting investment.
    Exception: When grouping by portfolio, keep empty portfolios visible (their header row shows with 0 count)
  */
  groups.forEach(g => {
    g.values = g.values.filter(inv => inv.investmentId);
  });

  if (group !== 'by-portfolio') {
    if (groups && groups.length > 0) {
      groups = groups.filter(g => g.values.length > 0);
    }
  }

  const [columns, setColumns] = useState();
  const hash = useHash() || '';
  const hashParts = hash
    .substring(1)
    .split('/')
    .filter(part => part);
  const hashRoot = hashParts[0];
  const impactReceiptInvestmentId = hashRoot === 'ir' && parseInt(hashParts[1]);

  const togglewNewPortfolioDrawer = useCallback(
    (e, id) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const portfolioData =
        (query || []).filter(row => row.portfolioId === id)?.[0] || {};
      const basePortfolio = portfolioData?.mainPortfolioId || id;
      setShowNewDonationDrawer(false);
      setSelectedPorfolioId(
        typeof basePortfolio === 'number' ? basePortfolio : undefined,
      );
      setShowNewPortfolioDrawer(prev => !prev);
    },
    [query],
  );

  const confirmReplacePortfolio = useCallback((type, value) => {
    setConfirmation({ type, value });
  }, []);

  const togglewNewDonationDrawer = useCallback(initialValues => {
    const { donation, portfolio } = currentDrawer;
    if (
      (showNewDonationDrawer && donation.touched) ||
      (showNewPortfolioDrawer && portfolio.touched)
    ) {
      confirmReplacePortfolio('donation', initialValues);
      setShowNewPortfolioDrawer(false);
      return;
    }
    setShowNewDonationDrawer(false);
    setShowNewPortfolioDrawer(false);
    if (!showNewDonationDrawer && initialValues) {
      setDonationInitialValues(initialValues);
    }
    if (!initialValues) {
      setDonationInitialValues();
    }
    setShowNewDonationDrawer(!showNewDonationDrawer);
  }, []);

  const onCloseDonationDrawer = useCallback(() => {
    setShowNewDonationDrawer(false);
  }, []);

  const onClosePortfolioDrawer = useCallback(() => {
    setShowNewPortfolioDrawer(false);
  }, []);

  const toggleManagerDrawer = useCallback(() => {
    setShowNewDonationDrawer(false);
  }, []);

  const toggleImpactReceiptDrawer = useCallback(
    (title, investmentId) => {
      setIrTitle(title);
      setSelectedInvestmentId(investmentId);
      setShowImpactReceiptDrawer(!showImpactReceiptDrawer);
      if (showImpactReceiptDrawer) {
        push(pathname + searchParams);
      }
    },
    [push, pathname, searchParams, showImpactReceiptDrawer],
  );

  const handleTitleClick = useCallback(
    ({ values, ...rest }) => {
      switch (group) {
        case 'by-manager':
          toggleManagerDrawer();
          break;
        case 'by-npo':
          toggleImpactReceiptDrawer('Impact Reciept', values[0]?.investmentId);
          break;
        case 'by-portfolio':
          setSelectedPorfolioId(
            values[0]?.portfolioId || values[0]?.id || rest.groupHeader,
          );
          setShowNewPortfolioDrawer(true);
          setShowNewDonationDrawer(false);
          break;
      }
    },
    [portfoliosList],
  );

  /* Fix: VirtualizedView does not return the id of a portfolio if it does
  not have investments, so, this uses its name to find the id and be able to edit.
  */
  useEffect(() => {
    if (selectedPorfolioId && typeof selectedPorfolioId === 'string') {
      const id = portfolios.filter(
        ({ name }) => name === selectedPorfolioId,
      )?.[0]?.id;
      if (id) {
        setSelectedPorfolioId(id);
      }
    }
  }, [selectedPorfolioId]);

  useEffect(() => {
    if (impactReceiptInvestmentId) {
      toggleImpactReceiptDrawer('Impact Receipt', impactReceiptInvestmentId);
    }
  }, [impactReceiptInvestmentId]);

  const handleConfirm = useCallback(() => {
    setConfirmation();
    setShowNewDonationDrawer(false);
    setShowNewPortfolioDrawer(false);
    if (confirmation.type === 'portfolio') {
      setSelectedPorfolioId(confirmation.value);
      setTimeout(() => {
        setShowNewPortfolioDrawer(true);
      }, 200);
    }
    if (confirmation.type === 'donation') {
      setDonationInitialValues(confirmation.value);
      setTimeout(() => {
        setShowNewDonationDrawer(true);
        setShowNewPortfolioDrawer(false);
      }, 200);
    }
  }, []);

  const handleCancel = useCallback(() => {
    setConfirmation();
  }, []);

  const handleOnPressIntroOption = useCallback(
    (optionIndex, hideIntroNextTime) => {
      handleCloseIntro(hideIntroNextTime);
      switch (optionIndex) {
        case 1:
          togglewNewDonationDrawer();
          break;
        case 2:
          togglewNewDonationDrawer();
          break;
        case 3:
          togglewNewDonationDrawer();
          break;
        default:
          togglewNewDonationDrawer();
      }
    },
    [togglewNewDonationDrawer],
  );

  useEffect(() => {
    setColumns(
      tableCols
        .filter(({ field }) => !field || field !== groupByField(group))
        .map(col => ({ ...col, sortable: true })),
    );
  }, [group]);

  const [summaryBarMode, setSummaryBarMode] = useState('INFO');

  const titleMapper = useMemo(() => makeGroupTitleMapper(group), [group]);

  const rowGridRenderer = useCallback(
    ({
      value,
      columns,
      style, // Style object to be applied to row (to position it)
      selectedRows,
      setSelectedRows,
      hasCheckBox,
      isLastRow,
      index,
    }) => {
      return (
        <ContributionsRow
          style={style}
          group={group}
          value={value}
          columns={columns}
          columnTypes={columnTypes}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          hasCheckBox={hasCheckBox}
          isLastRow={isLastRow}
          index={index}
          groupKeyField={groupByField(group)}
        >
          {value?.report && (
            <QuickViewButton
              onClick={() =>
                toggleImpactReceiptDrawer('Impact Reciept', value?.investmentId)
              }
            >
              Quick View
            </QuickViewButton>
          )}
        </ContributionsRow>
      );
    },
    [group, columns],
  );

  const handleRenderRow = rowGridRenderer;

  /*const managers = uniqBy([
    ...accounts,
    ...(data || []).map(({relationshipManager, relationshipManagerId}) => ({
      id: relationshipManagerId,
      name: `${relationshipManager?.firstName} ${relationshipManager?.lastName} `
    })
  ),
  ], (e) => {
    return e.id;
  });*/

  const searchOptions =
    data &&
    uniq(
      quickFilterFields.reduce(
        (all, field) => [...all, ...data.map(row => get(row, field))],
        [],
      ),
    )
      .filter(option => option)
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  const [selection, setSelection] = useState({});
  const flatSelectedRows = Object.values(selection).reduce(
    (all, rows) => all.concat(rows),
    [],
  );

  const handleContactManagers = useCallback(
    list => {
      const emails = flatSelectedRows
        .filter(({ programManager }) => programManager?.email)
        .map(({ programManager }) => programManager.email);
      if (!emails.length) {
        setShowContactManagerFail(true);
      } else {
        window.open(
          `mailto:${effectiveUser.attributes.email}?bcc=${list.join(';')}`,
          '_blank',
        );
      }
    },
    [effectiveUser, flatSelectedRows],
  );

  const handleCreateMenuItemClick = useCallback(
    id => {
      switch (id) {
        case 'newDonation':
          togglewNewDonationDrawer();
          break;
        case 'newPortfolio':
          togglewNewPortfolioDrawer();
          break;
      }
    },
    [togglewNewDonationDrawer, togglewNewPortfolioDrawer],
  );

  const handleFilterValue = useCallback(() => {
    try {
      const { search } = window.location;
      const query = searchToQuery(search);
      setFilters(
        [
          'npo',
          'portfolio',
          'manager',
          'status',
          'service',
          'donation-size',
          'year',
          'archive',
          'stage',
        ]
          .filter(name => query[name])
          .map(name => [name, query[name]])
          .reduce(
            (all, [name, values]) => [
              ...all,
              ...JSON.parse(values).map(value => ({ filter: name, value })),
            ],
            [],
          ),
      );
    } catch (err) {
      alert(err);
    }
  }, []);

  useEffect(() => {
    handleFilterValue();
  }, [handleFilterValue]);

  const handleRenderGroupHeader = useCallback(
    ({
      style,
      value,
      isScrolling,
      isVisible,
      selectedRows,
      setSelectedRows,
      onExpandCollapse,
    }) => {
      return (
        <GroupHeader
          groupKey={value.groupHeader}
          values={value.values}
          title={
            group === 'by-portfolio' && partnerQueueNames.has(value.groupHeader)
              ? `${titleMapper(value.groupHeader)} — Queue`
              : titleMapper(value.groupHeader)
          }
          itemAmount={value.values.filter(v => v.investmentId).length}
          view={view}
          style={style}
          isScrolling={isScrolling}
          isVisible={isVisible}
          group={group}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          onExpandCollapse={onExpandCollapse}
          togglewNewDonationDrawer={togglewNewDonationDrawer}
          toggleImpactReceiptDrawer={toggleImpactReceiptDrawer}
          togglePortfolioDrawer={togglewNewPortfolioDrawer}
          onTitleClick={() => handleTitleClick(value)}
          overdueOnlyFilter={quickFilter === 'overdue'}
          isExpanded={value.isExpanded}
          {...{ summaryBarMode }}
        />
      );
    },
    [view, group, summaryBarMode, partnerQueueNames],
  );

  const columnsWithEvents = useMemo(
    () =>
      columns?.map(column =>
        column.field !== 'amount'
          ? column
          : {
              ...column,
              sortable: true,
              onCellClicked: ({ data }) => {
                const allocations = query
                  .filter(
                    ({ investmentId }) => investmentId === data.investmentId,
                  )
                  .map(row => ({
                    amount: row?.allocationAmount ?? 0,
                    name: row?.report?.name ?? row?.programName,
                  }));
                setDonationInitialValues({
                  id: data.id,
                  allocations: allocations || [],
                  granteeName: data.grantee?.name ?? '-',
                  donationMessage: data.message,
                  portfolio: data.portfolioId,
                  manager: data.relationshipManagerId || data.managerId || 0,
                  cash: data.cashAmount,
                  goods: data.goodsAmount,
                  volunteerism: data.volunteerismAmount,
                  services: data.servicesAmount,
                  tags: [],
                  status: data.status,
                  programName: data?.programName ?? '-',
                  programNameByDonor: data?.programNameByDonor,
                  externalId: data?.externalId || '',
                  invitationStatus: getInvitationStatus(data),
                  invitationCreatedAt: data?.invitationCreatedAt,
                  year: data?.year,
                  portfolio_report_date_1: data?.reportDate1,
                  portfolio_report_date_2: data?.reportDate2,
                  portfolio_report_date_3: data?.reportDate3,
                  portfolio_report_date_4: data?.reportDate4,
                  portfolio_report_date_5: data?.reportDate5,
                  portfolio_report_date_6: data?.reportDate6,
                });
                setShowNewPortfolioDrawer(false);
                setShowNewDonationDrawer(true);
              },
            },
      ),
    [columns, query],
  );

  if (!isLoading && data) {
    addTiming('afterQuery', ['beforeQuery']);

    if (addTiming('render3', ['render2'])) {
      //const loadTime = (timing.render1 - timing.beforeQuery) / 1000
      //const queryTime = (timing.afterQuery - timing.beforeQuery) / 1000
      //alert(`UI load time: ${loadTime}s (query ${Math.round(queryTime * 100 / loadTime)}%)`)
    }
    addTiming('render2', ['render1']);
    addTiming('render1', ['render0']);
    addTiming('render0');
  }

  /*
    if (isLoading && !data) {
      return <LinearProgress variant="query"/>
    }
  */
  return (
    <HomeInner
      columns={columnsWithEvents}
      {...{
        isLoading,
        filters,
        customPortal,
        classes,
        flatSelectedRows,
        searchOptions,
        handleSearchInput,
        group,
        handleCreateMenuItemClick,
        showNewPortfolioDrawer,
        onClosePortfolioDrawer,
        services,
        selectedPorfolioId,
        showNewDonationDrawer,
        portfolios,
        accounts,
        onCloseDonationDrawer,
        donationInitialValues,
        toggleImpactReceiptDrawer,
        me,
        showManagerDrawer,
        toggleManagerDrawer,
        showImpactReceiptDrawer,
        irTitle,
        selectedInvestmentId,
        yearFilter,
        groups,
        query,
        showIntro,
        handleCloseIntro,
        handleOnPressIntroOption,
        confirmation,
        handleCancel,
        handleConfirm,
        handleFilterValue,
        handleContactManagers,
        view,
        handleClearPageSettings,
        isSortDescending,
        handleToggleSort,
        refresh,
        summaryBarMode,
        setSummaryBarMode,
        counter,
        handleRenderGroupHeader,
        handleRenderRow,
        setSelection,
        serviceType,
        setServiceType,
        showContactManagerFail,
        setShowContactManagerFail,
        portfoliosList,
        user,
      }}
    />
  );
};

const HomeInner = memo(
  ({
    isLoading,
    filters,
    flatSelectedRows,
    searchOptions,
    handleSearchInput,
    group,
    handleCreateMenuItemClick,
    showNewPortfolioDrawer,
    onClosePortfolioDrawer,
    services,
    selectedPorfolioId,
    showNewDonationDrawer,
    portfolios,
    accounts,
    onCloseDonationDrawer,
    donationInitialValues,
    toggleImpactReceiptDrawer,
    me,
    showManagerDrawer,
    toggleManagerDrawer,
    showImpactReceiptDrawer,
    irTitle,
    selectedInvestmentId,
    groups,
    query,
    showIntro,
    handleCloseIntro,
    handleOnPressIntroOption,
    confirmation,
    handleCancel,
    handleConfirm,
    handleFilterValue,
    handleContactManagers,
    view,
    columns,
    counter,
    refresh,
    handleRenderGroupHeader,
    handleRenderRow,
    setSelection,
    serviceType,
    setServiceType,
    showContactManagerFail,
    setShowContactManagerFail,
    portfoliosList,
    user,
  }) => {
    /*const useFilterHandler = callback => useCallback(value => {
    callback(value)
  }, [])*/
    const [showInvitationPopup, setShowInvitationPopup] = useState(false);
    const [invitationRecipients, setInvitationRecipients] = useState({});
    const [showInviteConfirm, setShowInviteConfirm] = useState(false);
    const [showInviteFail, setShowInviteFail] = useState(false);
    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [moveToPortfolioOpen, setMoveToPortfolioOpen] = useState(false);
    const [moveTargetPortfolioId, setMoveTargetPortfolioId] = useState('');
    const [moveLoading, setMoveLoading] = useState(false);
    const [dueDatesOpen, setDueDatesOpen] = useState(false);
    const [dueDatesLoading, setDueDatesLoading] = useState(false);
    const [dueDateValues, setDueDateValues] = useState({});
    const { run: runInvitation } = useNewInvitation();

    //  const anyUnallocated = flatSelectedRows
    //    .some(({ allocationId }) => !allocationId)

    const handleExportCsv = useCallback(() => {
      const headers = [
        'Organization',
        'Funded Program Name',
        'Cash Amount',
        'Goods Amount',
        'Volunteerism Amount',
        'Services Amount',
        'Contact First Name',
        'Contact Last Name',
        'Contact Email (NPO)',
        'External Record ID',
        'Relationship Manager Email (Donor)',
        'Allocate Due Date',
        'Submit Due Date',
        'Resolve Due Date',
        'Publish Due Date',
        'Interim Due Date',
        'Finalize Due Date',
        'Portfolio Name',
        'Investment Year',
        'Tag',
        'Tag 2',
        'Tag 3',
        'Tag 4',
        'Tag 5',
        'Tag 6',
        'Tag 7',
        'Tag 8',
        'Tag 9',
        'Tag 10',
      ];

      const escapeCsv = val => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csvRows = flatSelectedRows.map(row => {
        let tags = [];
        try {
          tags = JSON.parse(row.tags || '[]');
        } catch (_) {
          tags = [];
        }
        const invitation = row.invitations?.[0] || {};
        return [
          invitation.organization || row.grantee?.name || '',
          row.programNameByDonor || row.programName || '',
          row.cashAmount ?? '',
          row.goodsAmount ?? '',
          row.volunteerismAmount ?? '',
          row.servicesAmount ?? '',
          invitation.firstName || '',
          invitation.lastName || '',
          invitation.email || '',
          row.externalId || '',
          row.relationshipManager?.email || row.managerEmail || '',
          row.reportDate1 || '',
          row.reportDate2 || '',
          row.reportDate3 || '',
          row.reportDate4 || '',
          row.reportDate5 || '',
          row.reportDate6 || '',
          row.name || '',
          row.year || '',
          ...Array.from({ length: 10 }, (_, i) => tags[i] || ''),
        ].map(escapeCsv).join(',');
      });

      const csv = [headers.join(','), ...csvRows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'investments.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }, [flatSelectedRows]);

    const client = useClient();
    const handleDeleteSelected = useCallback(async () => {
      let numSuccess = 0;
      let numErrors = 0;
      for (const { allocationId, investmentId } of flatSelectedRows) {
        if (allocationId || investmentId) {
          try {
            await client.mutate({
              mutation: deleteInvestmentMutation,
              variables: {
                id: investmentId,
              },
            });
          } catch {
            numErrors++;
          }
          numSuccess++;
        }
      }

      // Will delete the porfolio at the end if all investments are deleted
      // await runDelete({ id: portfolioIdToDelete });

      refresh();
      if (numErrors > 0) {
        alert(
          `${numSuccess} investments were deleted successfully` + numErrors
            ? `, and ${numErrors} failed`
            : '',
        );
      }
    }, [client, flatSelectedRows, refresh]);

  const handleMoveToPortfolio = useCallback(async () => {
    if (!moveTargetPortfolioId) return;
    setMoveLoading(true);
    for (const { investmentId } of flatSelectedRows) {
      if (investmentId) {
        try {
          await client.mutate({
            mutation: moveToPortfolioMutation,
            variables: { id: investmentId, portfolioId: Number(moveTargetPortfolioId) },
          });
        } catch {
          // no-op
        }
      }
    }
    setMoveLoading(false);
    setMoveToPortfolioOpen(false);
    setMoveTargetPortfolioId('');
    refresh();
  }, [client, flatSelectedRows, moveTargetPortfolioId, refresh]);

  const handleUpdateDueDates = useCallback(async () => {
    setDueDatesLoading(true);
    const dateEntries = [
      'reportDate1', 'reportDate2', 'reportDate3', 'reportDate4', 'reportDate5',
    ].filter(k => dueDateValues[k]);
    const results = await Promise.all(
      flatSelectedRows
        .filter(({ investmentId, portfolioId }) => investmentId && portfolioId)
        .map(({ investmentId, portfolioId }) => {
          const variables = { id: investmentId, portfolioId: Number(portfolioId) };
          dateEntries.forEach(k => { variables[k] = dueDateValues[k]; });
          return client.mutate({ mutation: updateInvestmentMutation, variables }).then(() => true).catch(() => false);
        })
    );
    const numErrors = results.filter(ok => !ok).length;
    setDueDatesLoading(false);
    setDueDatesOpen(false);
    setDueDateValues({});
    if (numErrors > 0) {
      setSnackbarMessage(`${numErrors} investment(s) failed to update.`);
    } else {
      setSnackbarMessage('Due dates updated successfully.');
    }
    setSnackbarOpen(true);
    refresh();
  }, [client, flatSelectedRows, dueDateValues, refresh]);

    const parseRecipientRow = (inv, row) => {
      return {
        ...inv,
        portfolio: row?.name,
        year: row?.year,
        investmentId: row.investmentId,
        amount:
          row?.amount ?? row?.allocationAmount ?? row?.investmentTotal ?? 0,
        program: row?.programName || 'program',
        submitDue: row?.reportDate2 || 'submit due date',
        resolveDue: row?.reportDate3 || 'resolve due date',
      };
    };

    const handleInviteNpo = () => {
      const recipients = {
        uninvited: [],
        unallocated: [],
        managers: [],
      };
      flatSelectedRows.forEach(row => {
        const invitations = row.invitations || row.invitation || [];
        if (invitations.length === 0) {
          return;
        }
        const status = row.status ? row.status.toUpperCase() : '';
        if (status === 'REJECTED') {
          return;
        }
        invitations.forEach(inv => {
          // An invitation is considered "invited" once it has been sent
          // (its notification date is set). The REST /v4/home payload exposes
          // that timestamp as `dt`. Investment status is not reliable here
          // because a manual send does not update investment.status.
          const alreadyInvited = Boolean(
            inv.dt ||
              inv.notificationDt ||
              inv.notification_dt ||
              row.invitationDt,
          );
          const isAllocated = Boolean(row.allocationId) || status === 'ALLOCATED';
          let bucket;
          if (isAllocated) {
            bucket = 'managers';
          } else if (alreadyInvited) {
            // Invited but the investment is not allocated yet.
            bucket = 'unallocated';
          } else {
            bucket = 'uninvited';
          }
          const list = recipients[bucket];
          const isNew = list.every(
            recipient =>
              recipient.email !== inv.email ||
              recipient.amount !== row.amount ||
              recipient.investmentId !== row.id,
          );
          if (isNew) {
            list.push(parseRecipientRow(inv, row));
          }
        });
      });

      // Check if there are any recipients to process
      const hasRecipients =
        recipients.uninvited.length +
        recipients.unallocated.length +
        recipients.managers.length;

      if (hasRecipients > 0) {
        setInvitationRecipients(recipients);
        setShowInvitationPopup(true);
      } else {
        setShowInviteFail(true);
      }
    };

    const parseText = (
      text,
      firstName,
      lastName,
      year,
      portfolio,
      organization,
      amount,
      userFirst,
      userLast,
      userEmail,
      account,
      phone = '-',
      inviteeEmail = '-',
      program = '-',
      submitDue = '-',
      resolveDue = '-',
    ) => {
      const replaced = text
        .replace(/\[investmentRecipientFirstName\]/g, firstName)
        .replace(/\[investmentRecipientLastName\]/g, lastName)
        .replace(/\[portfolioYear\]/g, year)
        .replace(/\[portfolioName\]/g, portfolio)
        .replace(/\[investmentRecipientOrganization\]/g, organization)
        .replace(/\[investmentAmount\]/g, amount)
        .replace(/\[userFirst\]/g, userFirst)
        .replace(/\[userLast\]/g, userLast)
        .replace(/\[userEmail\]/g, userEmail)
        .replace(/\[userPhone\]/g, phone)
        .replace(/\[accountName\]/g, account)
        .replace(/\[investmentRecipientEmail\]/g, inviteeEmail)
        .replace(/\[investmentFundedProgramName\]/g, program)
        .replace(/\[submitForFeedback\]/g, submitDue)
        .replace(/\[resolveFeedback\]/g, resolveDue);
      return replaced;
    };

    const parseInvitation = (recipient, message, datetime, allowResend = false) => {
      return {
        automatic: !!datetime,
        datetime,
        allowResend,
        email: recipient.email,
        firstName: recipient.firstName,
        lastName: recipient.lastName,
        investmentId: recipient.investmentId,
        message: parseText(
          message,
          recipient.firstName,
          recipient.lastName,
          recipient.year,
          recipient.portfolio,
          recipient.organization,
          recipient.amount,
          me?.firstName ?? user.attributes['givenName'],
          me?.lastName ?? user.attributes['familyName'],
          me?.email ?? user.authUser.username,
          me?.name || 'Organization',
          me?.phone || '-',
          recipient.email,
          recipient.program,
          recipient.submitDue,
          recipient.resolveDue,
        ),
        organization: recipient.organization,
      };
    };

    const handleSubmitNpoInvitations = (message, datetime, selectedRecipients) => {
      const {
        uninvited = [],
        unallocated = [],
        managers = [],
      } = selectedRecipients || {};

      const invitations = [];
      uninvited.forEach(row => {
        invitations.push(parseInvitation(row, message, datetime, false));
      });
      unallocated.forEach(row => {
        invitations.push(parseInvitation(row, message, datetime, true));
      });
      managers.forEach(row => {
        invitations.push(parseInvitation(row, message, datetime, true));
      });

      let notSentCount = 0;
      let notSentReason = '';
      let completedCount = 0;
      const totalCount = invitations.length;

      const reportOutcome = () => {
        if (notSentCount === 0) {
          setShowInviteConfirm(true);
          return;
        }
        const plural = notSentCount > 1 ? 's were' : ' was';
        setSnackbarMessage(
          notSentCount === 1 && notSentReason
            ? notSentReason
            : `${notSentCount} invitation${plural} not sent (already sent, ` +
                'or the donation is already fully allocated).'
        );
        setSnackbarOpen(true);
      };

      invitations.forEach(invitation => {
        runInvitation(invitation, (data) => {
          const result = data?.newInvitation;
          // Nothing was emailed: either a duplicate send, or the donation is
          // already fully allocated so the backend declined to send.
          if (result?.alreadySent || result?.error) {
            notSentCount++;
            notSentReason = result?.error || notSentReason;
          }
          completedCount++;
          if (completedCount === totalCount) {
            refresh();
            reportOutcome();
          }
        });
      });
      setInvitationRecipients({});
      setShowInvitationPopup(false);
      if (totalCount === 0) {
        reportOutcome();
      }
    };
    const [invitationFields, setInvitationFields] = useState({});
    const [expanded, setExpanded] = useState(false);
    const handleInvitationField = useCallback((key, value) => {
      setInvitationFields(oldFields => ({
        ...oldFields,
        [key]: value,
      }));
    }, []);
    const virtualized = useRef();
    useEffect(() => {
      if (!virtualized) {
        return;
      }
      virtualized.current?.clearSelection();
    }, [group]);
    const [expandedGroups, handleExpansion] = useState({}); // eslint-disable-line
    //const numExpandedGroups = Object.keys(expandedGroups).length
    //const areAllExpanded = numExpandedGroups === groups?.length
    //const areAllCollapsed = numExpandedGroups === 0
    const handleCollapseAll = useCallback(() => {
      virtualized.current.collapseAll();
    }, [virtualized.current]);
    const handleExpandAll = useCallback(() => {
      virtualized.current.expandAll();
    }, [virtualized.current]);
    const handleExpand = () => {
      if (expanded) {
        handleCollapseAll();
      } else {
        handleExpandAll();
      }
      setExpanded(!expanded);
    };
    const serviceTypeTitle = useMemo(
      () => serviceTypeItems.find(({ value }) => value === serviceType)?.label,
      [serviceType],
    );
    const handleServiceTypeClick = useCallback(id => {
      setServiceType(id);
    }, []);
    const handleGetRowHeight = useCallback(() => 64 + 5, []);

    return (
      <HomePage noPadding fullHeight>
        <Header
          groups={groups}
          rows={query}
          {...{ counter, filters }}
          onFilterValue={handleFilterValue}
          onCreateMenuItemClick={handleCreateMenuItemClick}
          setShowMoreFilters={setShowMoreFilters}
          showMoreFilters={showMoreFilters}
          {...{ searchOptions, handleSearchInput, filters, columns }}
        />
        <PageTop>
          {isLoading && <LinearProgress variant="query" />}
          <ExpandAll onClick={handleExpand}>
            Expand/collapse all groups <UnfoldMoreIcon />
          </ExpandAll>
          {group === 'by-service' && (
            <DropDownMenu
              variant="outlined"
              buttonTitle={serviceTypeTitle}
              menuItems={serviceTypeItems}
              onMenuItemClick={handleServiceTypeClick}
            />
          )}
          {showNewPortfolioDrawer && (
            <NewPortfolio
              open={showNewPortfolioDrawer}
              onClose={onClosePortfolioDrawer}
              servicesList={services}
              portfolioId={selectedPorfolioId}
              portfoliosList={portfoliosList}
              {...{ refresh }}
            />
          )}
          {showNewDonationDrawer && (
            <NewDonation
              portfolios={portfolios}
              accounts={accounts}
              open={showNewDonationDrawer}
              onClose={onCloseDonationDrawer}
              donationInitialValues={donationInitialValues}
              toggleImpactReceiptDrawer={toggleImpactReceiptDrawer}
              me={me}
              {...{ refresh }}
            />
          )}
          <ManagerDetails
            open={showManagerDrawer}
            onClose={toggleManagerDrawer}
          />
          {showImpactReceiptDrawer && irTitle && selectedInvestmentId && (
            <ImpactReceiptDrawer
              title={irTitle}
              investmentId={selectedInvestmentId}
              open={showImpactReceiptDrawer}
              onClose={toggleImpactReceiptDrawer}
              width="75%"
            />
          )}
          {showInvitationPopup && (
            <InvitationPopup
              onChangeMessage={() => undefined}
              open={showInvitationPopup}
              onClose={() => setShowInvitationPopup(false)}
              errors={{}}
              values={invitationFields}
              setField={handleInvitationField}
              onSubmit={handleSubmitNpoInvitations}
              investmentRecipientFirstName={'FIRST NAME'}
              investmentRecipientLastName={'FIRST NAME'}
              investmentAmount={0}
              investmentRecipientOrganization={'ORGANIZATION'}
              portfolioYear={'-'}
              portfolioName={'-'}
              userFirst={''}
              userLast={''}
              userEmail={''}
              userPhone={''}
              accountName={'-'}
              npoRecipients={invitationRecipients}
            />
          )}
          <ConfirmationDialog
            header="Npo Invitations"
            openDialog={showInviteConfirm}
            onCloseDialog={() => setShowInviteConfirm(false)}
            actionButtonOne={() => setShowInviteConfirm(false)}
            textButtonOne="Ok"
            title="The requested NPO invitations has been sent"
          />

          <ConfirmationDialog
            header="Npo Invitations"
            openDialog={showInviteFail}
            onCloseDialog={() => setShowInviteFail(false)}
            actionButtonOne={() => setShowInviteFail(false)}
            textButtonOne="Ok"
            title={
              'There are no valid recipients on the selected Investments. ' +
              'Please complete your Investment information. To complete your ' +
              'recipients, you must provide at least a valid email address when ' +
              'updating your Investment.'
            }
          />

          <ConfirmationDialog
            header="Contact managers"
            openDialog={showContactManagerFail}
            onCloseDialog={() => setShowContactManagerFail(false)}
            actionButtonOne={() => setShowContactManagerFail(false)}
            textButtonOne="Ok"
            title="None of the selected Investments is allocated with a program that has a manager."
          />

          <Dialog
            open={moveToPortfolioOpen}
            onClose={() => !moveLoading && setMoveToPortfolioOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Move to Portfolio</DialogTitle>
            <DialogContent>
              <FormControl fullWidth style={{ marginTop: 8 }}>
                <InputLabel>Select Portfolio</InputLabel>
                <Select
                  value={moveTargetPortfolioId}
                  onChange={e => setMoveTargetPortfolioId(e.target.value)}
                  label="Select Portfolio"
                >
                  {portfolios.filter(p => !p.isPartnerQueue).map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}{p.year ? ` (${p.year})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setMoveToPortfolioOpen(false)}
                disabled={moveLoading}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleMoveToPortfolio}
                disabled={!moveTargetPortfolioId || moveLoading}
                variant="contained"
                color="primary"
              >
                {moveLoading ? 'Moving...' : 'Move'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={dueDatesOpen}
            onClose={() => { if (!dueDatesLoading) { setDueDatesOpen(false); setDueDateValues({}); } }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Update Due Dates</DialogTitle>
            <DialogContent>
              {[
                { key: 'reportDate1', label: 'Allocate Investment' },
                { key: 'reportDate2', label: 'Submit for Feedback' },
                { key: 'reportDate3', label: 'Resolve Feedback' },
                { key: 'reportDate4', label: 'Publish Report' },
                { key: 'reportDate5', label: 'Interim Report Update' },
              ].map(({ key, label }) => (
                <TextField
                  key={key}
                  label={label}
                  type="date"
                  fullWidth
                  style={{ marginTop: 16 }}
                  InputLabelProps={{ shrink: true }}
                  value={dueDateValues[key] || ''}
                  onChange={e => setDueDateValues(prev => ({ ...prev, [key]: e.target.value }))}
                />
              ))}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => { setDueDatesOpen(false); setDueDateValues({}); }}
                disabled={dueDatesLoading}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateDueDates}
                disabled={dueDatesLoading || !Object.values(dueDateValues).some(v => v)}
                variant="contained"
                color="primary"
              >
                {dueDatesLoading ? 'Updating...' : 'Update'}
              </Button>
            </DialogActions>
          </Dialog>

          {flatSelectedRows?.length !== 0 && (
            <>
              <BulkLabel>
                <span>Bulk Actions</span>
              </BulkLabel>

              <ButtonLower
                variant="outlined"
                color="primary"
                onClick={handleInviteNpo}
              >
                Invite
              </ButtonLower>
              <ButtonLower
                variant="outlined"
                color="primary"
                onClick={handleContactManagers}
              >
                Contact Managers
              </ButtonLower>
              <DialogButton
                variant="outlined"
                color="primary"
                label="Delete"
                text={
                  <div>
                    Are you sure you want to delete following investments? This
                    cannot be undone.
                    {flatSelectedRows.map(
                      ({
                        amount,
                        grantee,
                        investmentId,
                        allocationId,
                        year,
                      }) => (
                        <div key={[investmentId, allocationId].join('-')}>
                          {allocationId ? `$${amount}` : 'Unallocated'} to{' '}
                          {grantee?.name} on {year}
                        </div>
                      ),
                    )}
                  </div>
                }
                positiveLabel="Yes"
                negativeLabel="No"
                negativeButtonComponent={ButtonLower}
                positiveButtonComponent={SubmitLink}
                onSubmit={handleDeleteSelected}
              >
                {() => null}
              </DialogButton>
              {(user.role === 'superAdmin' || user.ghostedUsername) && (
                <ButtonLower
                  variant="outlined"
                  color="primary"
                  onClick={() => setMoveToPortfolioOpen(true)}
                >
                  Move to Portfolio
                </ButtonLower>
              )}
              <ButtonLower
                variant="outlined"
                color="primary"
                onClick={() => setDueDatesOpen(true)}
              >
                Update Due Dates
              </ButtonLower>
              <Button color="primary" startIcon={<ExportIcon />} onClick={handleExportCsv}>
                Export to CSV
              </Button>
            </>
          )}
        </PageTop>
        <Root>
          <VirtualizedView
            ref={virtualized}
            groups={groups}
            view={view}
            columns={columns}
            isLoading={isLoading}
            onRenderGroupHeader={handleRenderGroupHeader}
            onRenderRow={handleRenderRow}
            onSelectionChanged={setSelection}
            onExpansionChanged={handleExpansion}
            showMoreFilters={showMoreFilters}
            onGetRowHeight={handleGetRowHeight}
          />
        </Root>
        {
          <HomeIntroModal
            open={showIntro}
            onClose={handleCloseIntro}
            onPressItem={handleOnPressIntroOption}
          />
        }
        <ConfirmationDialog
          openDialog={!!confirmation}
          onCloseDialog={handleCancel}
          actionButtonOne={handleConfirm}
          actionButtonTwo={handleCancel}
          textButtonOne="Confirm"
          textButtonTwo="Cancel"
          title="Do you want to replace the current drawer? \n Any unsaved changes will be lost."
        />
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </HomePage>
    );
  },
);

HomeMain.propTypes = {
  match: PropTypes.any,
};

export default function Home({ customPortal }) {
  const screen = window.location.pathname.includes('custom')
    ? 'custom'
    : 'home';

  const user = useUser();
  const effectiveUser = user?.effectiveUser;
  const userId = effectiveUser?.attributes['custom:user_id'];
  const defaultUrl =
    userId && localStorage.getItem(`User${userId}.donorHomeUrl`);

  const Redirection =
    screen === 'custom' && customPortal?.data ? (
      <Redirect
        to={`/donor/${screen}/${JSON.parse(customPortal.data.template).url}`}
      />
    ) : defaultUrl ? (
      <Redirect to={defaultUrl} />
    ) : (
      <Redirect to={`/donor/${screen}/by-portfolio/grid`} />
    );

  return (
    <>
      <Route
        path={`/donor/${screen}/:group/:view`}
        render={props => <HomeMain {...props} customPortal={customPortal} />}
      />
      <Route exact path={`/donor/${screen}`}>
        {Redirection}
      </Route>
    </>
  );
}

/*const defaultColDef = {
  ...defaultColDefBase,
  resizable: true,
}*/

/*function getCellValue ({ data, field, valueGetter, valueFormatter, cellRenderer }) {
  let value

  if (field) {
    value = get(data, field)
  }
  if (valueGetter) {
    value = valueGetter({ data, value })
  }
  if (valueFormatter) {
    value = valueFormatter({ data, value })
  }
  if (cellRenderer) {
    value = <div
      dangerouslySetInnerHTML={{ __html: cellRenderer({ data, value })}}
    />
  }

  return value
}*/

/*const quickViewItems = [
  { value: 'null', label: 'Show All' },
  { value: 'donations', label: 'My Donations Only' },
  { value: 'overdue', label: 'Overdue Reports' },
  { value: 'feedback', label: 'Reports awaiting feedback' },
  { value: 'notify', label: 'Unsent donation notifications' },
]*/

/*const ButtonSpacer = styled.span`
  ${({ withBorder }) => withBorder
    ? 'border-left: 1px solid rgba(0,0,0,.1); margin: 0 5px;'
    : 'margin-left: 5px;'}
  height: 100%;
`*/

export const HomePage = styled.div`
  flex: 1 1 auto;
  ${({ noPadding }) => !noPadding && 'padding: 10px 20px;'}
  ${({ fullHeight }) =>
    fullHeight &&
    'height: calc(100vh - var(--app-header-height)); display: flex; flex-direction: column;'}
`;

export const PageTop = styled.div`
  background-color: #f9f9f9;
  padding: 10px 20px;
`;

const ExpandAll = styled.div`
  color: #0072a2;
  font-size: 16px;
  font-weight: regular;
  cursor: pointer;
  text-decoration: underline;
  float: right;
  svg {
    transform: translateY(7px);
  }
`;

/*const summaryBarModeItems = [
  { value: 'INFO', label: 'Show info KPIs for per portfolio' },
  { value: 'SCHEDULE', label: 'Show schedule KPIs for Portfolio' },
  { value: 'MONEY', label: 'Show money KPIs per Portfolio' },
]*/

const BulkLabel = styled.div`
  width: auto;
  margin-right: 10px;
  height: 40px;
  padding-top: 6px;
  float: left;
`;

const ContributionsRow = styled(RowGrid)`
  position: relative;
  background-color: ${({ index }) => (index % 2 ? '#ebf0f7' : '#f6f8fa')};

  .quick-view {
    position: absolute;
    top: 17px;
    right: -150px;
    transition: right 500ms ease-out, opacity 250ms ease-out;
    height: 30px;
    background-color: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blue(5px);
    opacity: 0;
    text-transform: none;
  }

  &:hover > .quick-view {
    right: 10px;
    opacity: 1;
  }
  .show-on-hover {
    opacity: 0;
    transition: opacity 200ms ease-out;
  }
  &:hover .show-on-hover {
    opacity: 1 !important;
  }
  .MuiCheckbox-root {
    color: #cbd6e2 !important;
  }
  .MuiCheckbox-root.Mui-checked {
    color: #f89915 !important;
  }
  a,
  svg {
    color: rgb(0, 114, 162) !important;
  }
`;

const QuickViewButton = styled(Button).attrs({
  className: 'quick-view',
  variant: 'outlined',
})`
  font-weight: bold !important;
`;
